import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Agents } from '../lib/api';
import { useOverviewWs } from '../lib/ws';
import type { AgentSummary } from '@freebuff/shared';
import { Stat, Pill, ProgressBar, EmptyState, Spinner } from '../components/Common';
import clsx from '../lib/clsx';

export function Overview() {
  const [agents, setAgents] = useState<AgentSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const { snapshot } = useOverviewWs();

  useEffect(() => {
    Agents.list()
      .then((r) => setAgents(r.agents))
      .finally(() => setLoading(false));
    const interval = setInterval(() => {
      Agents.list().then((r) => setAgents(r.agents)).catch(() => null);
    }, 15_000);
    return () => clearInterval(interval);
  }, []);

  // Mistura o snapshot WS na lista (substitui valores de cpu/mem/load quando online).
  const liveById = new Map<string, NonNullable<typeof snapshot>['agents'][number]>();
  if (snapshot) {
    for (const a of snapshot.agents) liveById.set(a.id, a);
  }

  const total = agents.length;
  const online = agents.filter((a) => {
    const live = liveById.get(a.id);
    return live ? live.online : a.online ?? (a.lastSeenAt != null && Date.now() - a.lastSeenAt < 30_000);
  }).length;
  const alertsOpen = agents.reduce((sum, a) => sum + (a.openAlerts ?? 0), 0);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Agentes" value={total} />
        <Stat label="Online agora" value={online} tone={online === total ? 'ok' : online === 0 ? 'error' : 'warn'} sub={total === 0 ? 'Adicione seu primeiro agente' : `${total - online} offline`} />
        <Stat label="Alertas abertos" value={alertsOpen} tone={alertsOpen === 0 ? 'ok' : 'warn'} />
        <Stat label="Coleta" value="5s" sub="Painel via WebSocket" />
      </div>

      <section className="card">
        <div className="card-header">
          <div>
            <h2 className="text-lg font-semibold">Agentes</h2>
            <p className="text-xs text-slate-400">Visão consolidada e em tempo real.</p>
          </div>
          <Link to="/agents" className="btn">Gerenciar</Link>
        </div>
        <div className="card-body">
          {loading ? <Spinner /> :
            agents.length === 0 ? (
              <EmptyState
                title="Nenhum agente ainda"
                hint="Crie um agente para começar a coletar métricas."
                action={<Link to="/agents" className="btn btn-primary">Adicionar agente</Link>}
              />
            ) : (
              <div className="grid gap-3">
                {agents.map((a) => {
                  const live = liveById.get(a.id);
                  const cpu = live?.cpuPct ?? null;
                  const mem = live?.memPct ?? null;
                  const isOnline = live?.online ?? a.online ?? false;
                  return (
                    <Link
                      key={a.id}
                      to={`/agents/${a.id}`}
                      className="group rounded-xl border border-border bg-bg-subtle hover:border-accent/40 transition p-4 grid grid-cols-1 md:grid-cols-12 gap-4 items-center"
                    >
                      <div className="md:col-span-4">
                        <div className="flex items-center gap-2.5">
                          <span className={clsx('w-2.5 h-2.5 rounded-full', isOnline ? 'bg-ok animate-pulseSoft' : 'bg-danger')} />
                          <span className="font-semibold text-white group-hover:text-accent transition">{a.name}</span>
                        </div>
                        <div className="text-xs text-slate-500 font-mono mt-1">{a.hostname ?? '—'}</div>
                      </div>
                      <div className="md:col-span-3">
                        <div className="text-[11px] text-slate-400 mb-1">CPU</div>
                        <div className="flex items-center gap-2">
                          <ProgressBar pct={cpu ?? 0} tone={cpuTone(cpu)} />
                          <span className="text-xs font-mono text-slate-300 w-12 text-right">{cpu != null ? `${cpu.toFixed(0)}%` : '—'}</span>
                        </div>
                      </div>
                      <div className="md:col-span-3">
                        <div className="text-[11px] text-slate-400 mb-1">Memória</div>
                        <div className="flex items-center gap-2">
                          <ProgressBar pct={mem ?? 0} tone={cpuTone(mem)} />
                          <span className="text-xs font-mono text-slate-300 w-12 text-right">{mem != null ? `${mem.toFixed(0)}%` : '—'}</span>
                        </div>
                      </div>
                      <div className="md:col-span-2 flex items-center justify-end gap-2">
                        {(a.openAlerts ?? 0) > 0
                          ? <Pill tone="error">{a.openAlerts} alerta{(a.openAlerts ?? 0) > 1 ? 's' : ''}</Pill>
                          : <Pill tone={isOnline ? 'ok' : 'error'}>{isOnline ? 'Online' : 'Offline'}</Pill>}
                      </div>
                    </Link>
                  );
                })}
              </div>
            )
          }
        </div>
      </section>
    </div>
  );
}

function cpuTone(pct: number | null): 'ok' | 'warn' | 'error' {
  if (pct == null) return 'ok';
  if (pct >= 90) return 'error';
  if (pct >= 70) return 'warn';
  return 'ok';
}
